<template>
    <div class="px-3 mt-4">
        <div class="mt-3 mb-8 space-y-2 max-w-7xl mx-auto">
            <p v-if="!!loadingError" class="my-2 error-msg">{{ loadingError }}</p>
            <div v-if="user && !loading">
                <modal
                    closable="true"
                    @close="migrateModalOpen = false"
                    :title="`Migrate User to Email`"
                    :open="migrateModalOpen"
                >
                    <div slot="content" class="max-w-md mx-auto">
                        <form class="space-y-4 mt-4" @submit.prevent="migrateAccountToEmail">
                            <div class="h-full flex flex-col justify-between">
                                <div class="space-y-2 flex-1" v-if="!migrateSuccess">
                                    <section class="flex-1 space-y-1 flex flex-col">
                                        <label class="text-sm">{{ $t('Email') }}</label>
                                        <input
                                            autocomplete="off"
                                            id="migrateEmail"
                                            type="email"
                                            v-model="migrateEmail"
                                            class="rounded border p-2"
                                        />
                                    </section>
                                    <section class="flex-1 space-y-1 flex flex-col">
                                        <label class="text-sm">{{ $t('Confirm Email') }}</label>
                                        <input
                                            autocomplete="email-confirm"
                                            id="migrateEmailConfirm"
                                            type="email"
                                            v-model="migrateEmailConfirm"
                                            class="rounded border p-2"
                                        />
                                    </section>
                                </div>
                                <p v-if="!!migrateError" class="my-2 error-msg">
                                    {{ migrateError }}
                                </p>
                                <p v-if="!!migrateSuccess" class="my-2 bg-green-100">
                                    {{ migrateSuccess }}
                                </p>
                                <div class="mt-6 mb-2 flex justify-between" v-if="!migrateSuccess">
                                    <Button
                                        :disabled="migrating"
                                        @click="migrateModalOpen = false"
                                        class="bg-gray-200 hover:bg-white hover:border-gray-800 border border-transparent px-6 py-2 rounded"
                                        type="button"
                                        label="Cancel"
                                    />
                                    <Button
                                        :loading="migrating"
                                        class="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded"
                                        :label="$t('Migrate')"
                                    />
                                </div>
                            </div>
                        </form>
                    </div>
                </modal>
                <modal
                    closable="true"
                    @close="closeEditModal"
                    :title="`Update User: ${user.name}`"
                    :open="editModalOpen"
                >
                    <div slot="content">
                        <form
                            class="space-y-4 mt-4"
                            style="height:500px;"
                            @submit.prevent="updateUser"
                        >
                            <div class="h-full flex flex-col justify-between">
                                <div class="space-y-4 flex-1">
                                    <section class="flex-1 space-y-1 flex flex-col">
                                        <label class="text-sm">{{ $t('Area') }}</label>
                                        <select
                                            v-if="areas"
                                            :disabled="submitting || submitted"
                                            class="form-input"
                                            v-model="area"
                                        >
                                            <option
                                                v-for="area in areas"
                                                :key="area.id"
                                                :value="area.id"
                                                >{{ area.tag }}</option
                                            >
                                        </select>
                                    </section>
                                    <section class="flex-1 space-y-1 flex flex-col">
                                        <label class="text-sm">{{ $t('Skills') }}</label>
                                        <v-select
                                            v-if="userSkills"
                                            class="flex-1 style-select z-10"
                                            placeholder="User Skills"
                                            :options="makeSelectableList(skills)"
                                            v-model="userSkills"
                                            multiple
                                        >
                                            <template v-slot:option="option">
                                                <div
                                                    class="z-50 w-full py-3 flex items-center space-x-3 focus:outline-none overflow-x-hidden"
                                                >
                                                    <span class="text-sm ml-2">
                                                        {{ option.tag }}
                                                    </span>
                                                </div>
                                            </template>
                                        </v-select>
                                    </section>
                                    <section class="flex-1 space-y-1 flex flex-col">
                                        <label class="text-sm">{{ $t('Groups') }}</label>
                                        <v-select
                                            v-if="userGroups"
                                            class="flex-1 style-select z-10"
                                            placeholder="User Groups"
                                            :options="groups"
                                            v-model="userGroups"
                                            multiple
                                        >
                                            <template v-slot:option="option">
                                                <div
                                                    class="z-50 w-full py-3 flex items-center space-x-3 focus:outline-none overflow-x-hidden"
                                                >
                                                    <span class="text-sm ml-2">
                                                        {{ option.tag }}
                                                    </span>
                                                </div>
                                            </template>
                                        </v-select>
                                    </section>
                                </div>
                                <p v-if="!!editError" class="my-2 error-msg">{{ editError }}</p>
                                <div class="mt-auto mb-2 flex justify-between">
                                    <Button
                                        :disabled="submitting"
                                        @click="closeEditModal"
                                        class="bg-gray-200 hover:bg-white hover:border-gray-800 border border-transparent px-6 py-2 rounded"
                                        type="button"
                                        label="Cancel"
                                    />
                                    <Button
                                        :loading="updating"
                                        class="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded"
                                        label="Update"
                                    />
                                </div>
                            </div>
                        </form>
                    </div>
                </modal>

                <modal
                    closable="true"
                    @close="closeNoteModal"
                    :title="`Add Note`"
                    :open="noteModalOpen"
                >
                    <div slot="content">
                        <form
                            class="space-y-4 mt-4"
                            style="height:230px;"
                            @submit.prevent="addNote"
                        >
                            <div class="h-full flex flex-col justify-between">
                                <div class="space-y-4 flex-1">
                                    <section class="flex-1 space-y-1 flex flex-col">
                                        <label class="text-sm">{{ $t('Memo') }}</label>
                                        <textarea
                                            v-model="note"
                                            class="rounded border p-2"
                                            rows="5"
                                        />
                                    </section>
                                </div>
                                <p v-if="!!noteError" class="my-2 error-msg">{{ noteError }}</p>
                                <div class="mt-auto mb-2 flex justify-between">
                                    <Button
                                        :disabled="submittingNote"
                                        @click="closeNoteModal"
                                        class="bg-gray-200 hover:bg-white hover:border-gray-800 border border-transparent px-6 py-2 rounded"
                                        type="button"
                                        label="Cancel"
                                    />
                                    <Button
                                        :loading="updatingNote"
                                        class="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded"
                                        :label="$t('Create')"
                                    />
                                </div>
                            </div>
                        </form>
                    </div>
                </modal>
                <modal
                    closable="true"
                    @close="closeEditModal"
                    :title="`Update User: ${user.name}`"
                    :open="editModalOpen"
                >
                    <div slot="content">
                        <form
                            class="space-y-4 mt-4"
                            style="height:500px;"
                            @submit.prevent="updateUser"
                        >
                            <div class="h-full flex flex-col justify-between">
                                <div class="space-y-4 flex-1">
                                    <section class="flex-1 space-y-1 flex flex-col">
                                        <label class="text-sm">{{ $t('Area') }}</label>
                                        <select
                                            v-if="areas"
                                            :disabled="submitting || submitted"
                                            class="form-input"
                                            v-model="area"
                                        >
                                            <option
                                                v-for="area in areas"
                                                :key="area.id"
                                                :value="area.id"
                                                >{{ area.tag }}</option
                                            >
                                        </select>
                                    </section>
                                    <section class="flex-1 space-y-1 flex flex-col">
                                        <label class="text-sm">{{ $t('Skills') }}</label>
                                        <v-select
                                            v-if="userSkills"
                                            class="flex-1 style-select z-10"
                                            placeholder="User Skills"
                                            :options="makeSelectableList(skills)"
                                            v-model="userSkills"
                                            multiple
                                        >
                                            <template v-slot:option="option">
                                                <div
                                                    class="z-50 w-full py-3 flex items-center space-x-3 focus:outline-none overflow-x-hidden"
                                                >
                                                    <span class="text-sm ml-2">
                                                        {{ option.tag }}
                                                    </span>
                                                </div>
                                            </template>
                                        </v-select>
                                    </section>
                                    <section class="flex-1 space-y-1 flex flex-col">
                                        <label class="text-sm">{{ $t('Groups') }}</label>
                                        <v-select
                                            v-if="userGroups"
                                            class="flex-1 style-select z-10"
                                            placeholder="User Groups"
                                            :options="groups"
                                            v-model="userGroups"
                                            multiple
                                        >
                                            <template v-slot:option="option">
                                                <div
                                                    class="z-50 w-full py-3 flex items-center space-x-3 focus:outline-none overflow-x-hidden"
                                                >
                                                    <span class="text-sm ml-2">
                                                        {{ option.tag }}
                                                    </span>
                                                </div>
                                            </template>
                                        </v-select>
                                    </section>
                                </div>
                                <p v-if="!!editError" class="my-2 error-msg">{{ editError }}</p>
                                <div class="mt-auto mb-2 flex justify-between">
                                    <Button
                                        :disabled="submitting"
                                        @click="closeEditModal"
                                        class="bg-gray-200 hover:bg-white hover:border-gray-800 border border-transparent px-6 py-2 rounded"
                                        type="button"
                                        label="Cancel"
                                    />
                                    <Button
                                        :loading="updating"
                                        class="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded"
                                        label="Update"
                                    />
                                </div>
                            </div>
                        </form>
                    </div>
                </modal>
            </div>

            <div
                v-if="user"
                class="md:flex space-y-3 md:space-y-0 items-center justify-between bg-gray-50 py-2 px-3"
            >
                <h4 class="text-lg text-center font-medium">User Details</h4>
                <div class="md:flex mr-6 gap-2 justify-end">
                    <button
                        class="group flex flex-shrink-0 items-center justify-center border text-gray-800 border-gray-500 hover:border-gray-900 text-sm rounded px-4 py-1 focus:outline-none focus:shadow-outline"
                        @click="editModalOpen = true"
                    >
                        <svg
                            class="w-5 h-5 text-gray-400 mr-1"
                            fill="none"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            ></path>
                        </svg>
                        {{ $t('Edit') }}
                    </button>
                    <button
                        class="group flex flex-shrink-0 items-center justify-center border text-red-800 border-red-500 hover:text-white hover:bg-red-500 text-sm rounded px-4 py-1 focus:outline-none focus:shadow-outline"
                        @click="toggleAdmin"
                    >
                        {{ user.role === 'admin' ? $t('Remove Admin') : $t('Make Admin') }}
                    </button>
                    <button
                        class="group flex flex-shrink-0 items-center justify-center border text-red-800 border-red-500 hover:text-white hover:bg-red-500 text-sm rounded px-4 py-1 focus:outline-none focus:shadow-outline"
                        @click="deleteModalOpen = true"
                    >
                        <svg
                            class="w-5 h-5 group-hover:text-red-100 text-red-300 mr-1"
                            fill="none"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            ></path>
                        </svg>
                        {{ $t('Delete') }}
                    </button>
                </div>
            </div>

            <div v-if="user">
                <modal @close="closeDeleteModal" :closable="!deleting" :open="deleteModalOpen">
                    <div slot="header">
                        <h2 class="pt-2 text-xl font-semibold text-red-600">
                            Delete User {{ user.name }}
                        </h2>
                    </div>
                    <div slot="content">
                        <form class="mt-4" @submit.prevent="deleteUser">
                            <p class="bg-red-100 rounded-sm px-3 py-4">
                                Are you sure you want to delete user {{ user.name }}
                                <br />
                                <span class="uppercase tracking-wide text-sm font-medium"
                                    >This action cannot be reversed!!</span
                                >
                            </p>
                            <input
                                required
                                v-model="deletePassword"
                                placeholder="Enter delete password"
                                class="w-full py-2 px-4 border mt-3"
                            />
                            <p v-if="!!deleteError" class="my-2 error-msg">{{ deleteError }}</p>
                            <div class="px-2 mt-6 mb-2 flex justify-between">
                                <Button
                                    :disabled="deleting"
                                    @click="closeDeleteModal"
                                    class="bg-gray-200 hover:bg-white hover:border-gray-800 border border-transparent px-6 py-2 rounded"
                                    type="button"
                                    :label="$t('Cancel')"
                                />
                                <Button
                                    :disabled="deleting"
                                    :loading="deleting"
                                    class="bg-red-500 hover:bg-red-600 text-white px-6 py-2 rounded"
                                    :label="$t('Yes Delete')"
                                />
                            </div>
                        </form>
                    </div>
                </modal>
            </div>

            <div v-if="user" class="space-y-8">
                <div
                    class="w-full flex flex-col lg:flex-row items-center space-x-6 focus:outline-none"
                >
                    <img
                        loading="lazy"
                        :alt="user.name"
                        style="max-width: 250px; max-height: 250px"
                        class="object-cover h-full w-full rounded-md border-4 border-gray-200 overflow-hidden"
                        :src="parseUrl(user.photoURL)"
                    />
                    <div class="flex-1">
                        <div class="">
                            <h3
                                class="text-2xl py-2 text-center lg:text-left font-medium flex items-center gap-3"
                            >
                                {{ user.name }}
                                <span
                                    v-if="user.pauseTime"
                                    class="text-red-600 font-bold bg-red-50 px-3 py-0.5 text-base rounded-sm"
                                    >giv停止</span
                                >
                            </h3>
                            <span>{{ user.job }}</span>
                        </div>
                        <div class="flex items-center py-1">
                            <span class="text-sm w-32 text-right mr-3">{{ $t('Groups') }}: </span>
                            <ul class="space-x-2 flex flex-wrap items-center">
                                <li
                                    v-for="s in userGroups"
                                    :key="s.id"
                                    class="border border-gray-500 rounded-full px-3 leading-6 text-sm font-medium text-gray-800"
                                >
                                    <span>{{ s.label }}</span>
                                </li>
                            </ul>
                        </div>
                        <div class="flex items-center py-1">
                            <span class="text-sm w-32 text-right mr-3">{{ $t('Area') }}: </span>
                            <span class="px-2 py-1 rounded-full leading-none">
                                {{ user.area }}</span
                            >
                        </div>
                        <div class="flex items-center py-1">
                            <span class="text-sm w-32 text-right mr-3"
                                >{{ $t('Created At') }}:
                            </span>
                            <span class="px-2 py-1 rounded-full leading-none">
                                {{ formatDate(user.createdAt) }}</span
                            >
                        </div>
                        <div class="flex items-center py-1">
                            <span class="text-sm w-32 text-right mr-3"
                                >{{ $t('Updated At') }}:
                            </span>
                            <span class="px-2 py-1 rounded-full leading-none">
                                {{ formatDate(user.updatedAt) }}</span
                            >
                        </div>
                        <div class="flex items-center py-1">
                            <span class="text-sm w-32 text-right mr-3">{{ $t('Login Email') }}: </span>
                            <span class="px-2 py-1 rounded-full leading-none">
                                {{ user.email }}</span
                            >
                        </div>
                        <div class="flex items-center py-1">
                            <span class="text-sm w-32 text-right mr-3">{{ $t('Status') }}: </span>
                            <span class="px-2 py-1 rounded-full leading-none">
                                {{ $t(user.status) }}</span
                            >
                        </div>
                        <div class="flex items-center py-1">
                            <span class="text-sm w-32 text-right mr-3"
                                >{{ $t('Giv Pause Time') }}:
                            </span>
                            <span class="px-2 py-1 rounded-full leading-none">
                                {{ user.pauseTime || 'なし' }}</span
                            >
                        </div>
                        <div class="flex items-center py-1">
                            <span class="text-sm w-32 text-right mr-3"
                                >{{ $t('Giv Send Preference') }}:
                            </span>
                            <span class="px-2 py-1 rounded-full leading-none">
                                {{ getGivPreferenceStr('send') }}</span
                            >
                        </div>
                        <div class="flex items-center py-1">
                            <span class="text-sm w-32 text-right mr-3"
                                >{{ $t('Giv Receive Preference') }}:
                            </span>
                            <span class="px-2 py-1 rounded-full leading-none">
                                {{ getGivPreferenceStr('receive') }}</span
                            >
                        </div>
                        <div class="flex items-center py-1" v-if="!user.migratedToEmail">
                            <span class="text-sm w-32 text-right mr-3">- </span>
                            <button
                                @click="migrateModalOpen = true"
                                class="group flex flex-shrink-0 items-center justify-center text-gray-800 text-xs underline px-3 focus:outline-none focus:shadow-outline bg-white"
                            >
                                {{ $t('Migrate User To Email') }}
                            </button>
                        </div>
                    </div>
                </div>
                <div class="border border-gray-100 rounded pb-2">
                    <div class="flex items-center justify-between bg-gray-50">
                        <h4 class="text-sm border-b border-gray-100 px-4 py-2 font-medium text-lg">
                            {{ $t('Memo') }}
                        </h4>

                        <button
                            class="group flex flex-shrink-0 items-center justify-center border text-gray-800 border-gray-500 hover:border-gray-900 text-sm rounded px-6 py-1 focus:outline-none focus:shadow-outline bg-white"
                            @click="noteModalOpen = true"
                        >
                            {{ $t('Create') }}
                        </button>
                    </div>

                    <ul class="mt-1" v-for="s in notes" :key="s.id">
                        <li class="px-2 py-1 rounded-full leading-none">
                            <UserNoteCard :note="s" :user="user" />
                        </li>
                    </ul>
                    <span
                        v-if="!notes || notes.length === 0"
                        class="block text-gray-500 text-center"
                        >{{ $t('No more items to show') }}</span
                    >
                </div>

                <div class="border border-gray-100 rounded pb-2">
                    <h4
                        class="text-sm bg-gray-50 border-b border-gray-100 px-4 py-2 font-medium text-lg"
                    >
                        {{ $t('Skills') }}
                    </h4>
                    <ul class="flex flex-col lg:flex-row flex-wrap px-2 mt-2">
                        <li
                            class="lg:w-1/2 xl:w-1/3 px-1 py-px"
                            v-for="s in user.skills || []"
                            :key="s.id"
                        >
                            <div
                                class="hover:shadow h-full border rounded border-gray-100 px-4 py-2 leading-none"
                            >
                                <span>{{ s.tag }}</span>
                            </div>
                        </li>
                    </ul>
                    <span
                        v-if="!user.skills || user.skills.length === 0"
                        class="block text-gray-500 text-center"
                        >{{ $t('No more items to show') }}</span
                    >
                </div>
                <div v-if="true" class="border border-gray-100 rounded pb-2">
                    <h4
                        class="text-sm bg-gray-50 border-b border-gray-100 px-4 py-2 font-medium text-lg"
                    >
                        {{ $t('Interests') }}
                    </h4>
                    <ul class="flex flex-col lg:flex-row flex-wrap px-2 mt-2">
                        <li
                            class="lg:w-1/2 xl:w-1/3 px-1 py-px"
                            v-for="s in user.interests || []"
                            :key="s.id"
                        >
                            <div
                                class="hover:shadow h-full border rounded border-gray-100 px-4 py-2 leading-none"
                            >
                                <span>{{ s.tag }}</span>
                            </div>
                        </li>
                    </ul>
                    <span
                        v-if="!user.interests || user.interests.length === 0"
                        class="block text-gray-500 text-center"
                        >{{ $t('No more items to show') }}</span
                    >
                </div>
                <div class="border border-gray-100 rounded pb-2">
                    <h4
                        class="text-sm bg-gray-50 border-b border-gray-100 px-4 py-2 font-medium text-lg"
                    >
                        {{ $t('Giv Given') }}
                    </h4>
                    <ul class="mt-1" v-for="s in user.givsGivenItems" :key="s.id">
                        <li class="px-2 py-1 rounded-full leading-none">
                            <GivCard :giv="s" :hideGiver="true" />
                        </li>
                    </ul>
                    <span
                        v-if="!user.givsGiven || user.givsGiven.length === 0"
                        class="block text-gray-500 text-center"
                        >{{ $t('No more items to show') }}</span
                    >
                </div>
                <div class="border border-gray-100 rounded pb-2">
                    <h4
                        class="text-sm bg-gray-50 border-b border-gray-100 px-4 py-2 font-medium text-lg"
                    >
                        {{ $t('Giv Received') }}
                    </h4>
                    <ul class="mt-1" v-for="s in user.givsReceivedItems" :key="s.id">
                        <li class="px-2 py-1 rounded-full leading-none">
                            <GivCard :giv="s" :hideReceiver="true" />
                        </li>
                    </ul>
                    <span
                        v-if="!user.givsReceived || user.givsReceived.length === 0"
                        class="block text-gray-500 text-center"
                        >{{ $t('No more items to show') }}</span
                    >
                </div>
                <div class="border border-gray-100 rounded pb-2">
                    <h4
                        class="text-sm bg-gray-50 border-b border-gray-100 px-4 py-2 font-medium text-lg"
                    >
                        {{ $t('Recent Notifications') }}
                    </h4>
                    <ul class="mt-1" v-for="s in recentNots" :key="s.id">
                        <li class="px-2 py-1 rounded-full leading-none">
                            <UserNotificationCard :notification="s" :user="user" />
                        </li>
                    </ul>
                    <span
                        v-if="!recentNots || recentNots.length === 0"
                        class="block text-gray-500 text-center"
                        >{{ $t('No more items to show') }}</span
                    >
                </div>
                <collapsible :title="$t('Dev Info')">
                    <div slot="content" class="px-6 bg-gray-100 overflow-x-auto">
                        <pre><code>{{ JSON.stringify(user, null, 2) }}</code></pre>
                    </div>
                </collapsible>
            </div>

            <spinner v-else />
        </div>
    </div>
</template>
<script>
import api from '../lib/api'
import GivCard from '../components/GivCard'
import UserNotificationCard from '../components/UserNotificationCard'
import UserNoteCard from '../components/UserNoteCard'
import { formatDistanceToNow } from 'date-fns'
export default {
    components: {
        GivCard,
        UserNotificationCard,
        UserNoteCard,
    },
    methods: {
        async toggleAdmin() {
            let msg = 'Are you sure you want to make this user admin?'
            if (this.user.role === 'admin') {
                msg = 'Are you sure you want to remove this user from admin?'
            }
            msg += '\nPlease enter your super admin password.'
            const password = window.prompt(msg)
            if (!password) return

            const value = this.user.role === 'member' ? true : false
            const resp = await api.toggleAdmin(this.user.id, value, password)

            window.location.reload()
        },
        getGivPreferenceStr(type) {
            const prefs = this.user.givPreferences
                ? this.user.givPreferences.filter(i => i.givType === type).map(i => i.tag)
                : []
            return prefs.join(', ')
        },
        formatDate(dateStr) {
            try {
                return `${formatDistanceToNow(new Date(dateStr), { addSuffix: true })}`
            } catch (err) {
                return null
            }
        },
        closeNoteModal() {
            this.noteModalOpen = false
            this.noteError = null
        },
        closeEditModal() {
            this.editModalOpen = false
            this.editError = null
        },
        closeDeleteModal() {
            this.deleteModalOpen = false
            this.deleteError = null
        },
        async migrateAccountToEmail() {
            if (this.migrateEmail !== this.migrateEmailConfirm) {
                this.migrateError = 'Emails are not the same'
                return
            }

            this.migrateError = null
            this.migrating = true
            const resp = await api.migrateUserToEmail(this.user.id, this.migrateEmail)
            if (resp.error) {
                this.migrateError = resp.error
            } else {
                this.migrateSuccess = 'OK'
            }
            this.migrating = false
            this.user.migratedToEmail = true
        },
        async addNote() {
            this.updatingNote = true
            this.noteError = null
            try {
                const resp = await api.addNote(this.id, this.note)
                this.noteModalOpen = false
                this.notes = [{ note: this.note, id: resp.id }, ...this.notes]
                this.note = ''
            } catch (err) {
                this.noteError = err.message
            }
            this.updatingNote = false
        },
        async updateUser() {
            this.updating = true
            this.editError = null
            try {
                const payload = {
                    areaId: this.area,
                    skills: this.userSkills.map(s => s.id),
                    groups: this.userGroups.map(s => s.id),
                }
                await api.updateUser(this.id, payload, this.user)
                this.updating = false
                this.editModalOpen = false
                this.user.area = payload.area
                this.user.skills = this.userSkills
            } catch (err) {
                this.editError = err.message
                this.updating = false
            }
        },
        async deleteUser() {
            this.deleting = true
            try {
                await api.deleteUser(this.id, this.deletePassword)
                this.deleting = false
                this.deleteModalOpen = false
                this.$router.replace({ path: '/users', query: { reload: true } })
            } catch (err) {
                this.deleteError = err.message
                this.deleting = false
            }
        },
        parseUrl(url) {
            if (!url) return url
            if (url.startsWith('https://cdn.giv.link')) {
                return url
            }
            if (url.startsWith('http')) {
                //@Todo common parseUrl in utils with this type=large query
                return url + '?type=large'
            }
            return `https://cdn.giv.link/${url}`
        },
        makeSelectableList(items) {
            return items ? items.map(i => ({ label: i.tag, ...i })) : []
        },
        setSelected(items) {
            console.log('selected:', items)
        },
    },
    async mounted() {
        try {
            const [user, recNots, areas, skills, groups, notes] = await Promise.all([
                api.getUser(this.id),
                api.getRecentNotificationsForUser(this.id),
                api.getAreas(),
                api.getSkills(),
                api.getGroups(),
                api.getNotes(this.id),
            ])
            this.user = user
            this.recentNots = recNots
            this.notes = notes
            this.areas = areas
            this.skills = this.makeSelectableList(skills)
            this.groups = this.makeSelectableList(groups)
            this.area = this.areas.find(i => i.id === this.user.areaId).id
            this.userSkills = this.makeSelectableList(this.user.skills)
            this.userGroups = this.makeSelectableList(
                Object.values(this.user.groups || {}).map(g => ({ id: g.id, label: g.tag })),
            )
            this.loading = false
        } catch (err) {
            console.log('error:', err.message)
            this.loadingError = err.message
            this.loading = false
        }
    },
    data() {
        return {
            migrating: false,
            migrateModalOpen: false,
            migrateEmail: '',
            migrateEmailConfirm: '',
            migrateError: null,
            migrateSuccess: null,

            user: null,
            note: null,
            deleting: false,
            updating: false,
            updatingNote: false,
            submitted: false,
            submitting: false,
            submittingNote: false,
            editError: null,
            noteError: null,
            loadingError: null,
            loading: true,
            areas: null,
            area: null,
            skills: [],
            userSkills: null,
            userGroups: null,
            editModalOpen: false,
            noteModalOpen: false,
            deleteModalOpen: false,
            deletePassword: '',
            deleteError: null,
            id: this.$route.params.id,
        }
    },
}
</script>
